// deps
import React, { memo, useCallback, useMemo } from "react";

// Interfaces
import { IRewardWidgetResponseData } from "@swiggy-private/checkout-types";

// Components
import { BoosterWidget } from "./BoosterWidget";
import { RewardsWidgetContent } from "./RewardsWidgetContent";

//helper
import { isRestaurantHaving2xDineCash } from "@dineout-cart-ui/helpers/index";
import { IColors } from "@swiggy-private/dls-web-common/dist/components/types.internal";

export interface IRewardsWidgetProps {
    rewardsWidget: IRewardWidgetResponseData;
    isSelected: boolean;
    isScenesFlow?: boolean;
    onToggleSwitch: () => void;
    // Analytics props
    eventId: string;
    dealCategory: string;
    restaurantId: number;
    billAmount: number;
    cashbackPerc: number;
    couponApplied?: boolean;
    couponDiscount?: number;
    onImpressionCashBackEarned: (props: {
        eventId: string;
        dealCategory: string;
        restaurantId: number;
        billAmount: number;
        cashbackToBeEarned: number;
        cashbackPerc: number;
        isRestaurantHaving2xDineCash?: boolean;
    }) => void;
    onImpressionCashBackToggle: (props: {
        eventId: string;
        dealCategory: string;
        restaurantId: number;
        billAmount: number;
        cashbackToSpend: number;
        cashbackToEarn: number;
        toggleState: string;
        remainingBalance: number;
        cashbackPerc: number;
        couponApplied: boolean;
        couponDiscount: number;
        isRestaurantHaving2xDineCash?: boolean;
    }) => void;
}

export const RewardsWidget: React.FC<IRewardsWidgetProps> = (props: IRewardsWidgetProps) => {
    const {
        rewardsWidget,
        isSelected,
        onToggleSwitch,
        isScenesFlow,
        // Analytics props
        eventId,
        dealCategory,
        restaurantId,
        billAmount,
        cashbackPerc,
        couponApplied = false,
        couponDiscount = 0,
        onImpressionCashBackEarned,
        onImpressionCashBackToggle,
    } = props;

    const {
        backgroundImage,
        earnedRewards,
        earnRewardsTitle,
        disableEarnRewardsTitle,
        logo,
        redeemableRewards,
        subtitle,
        title,
        extraInfoText,
        boosterBackgroundImage,
        boosterAnimation,
        header,
        isBooster,
        isToggleDisabled = false,
        bottomNudgeTextCallout,
    } = rewardsWidget;

    const isBoosterActive = useMemo(() => isRestaurantHaving2xDineCash(header, isBooster), [header, isBooster]);

    const handleCashBackEarnedImpression = useCallback(() => {
        onImpressionCashBackEarned({
            eventId,
            dealCategory,
            restaurantId,
            billAmount,
            cashbackToBeEarned: rewardsWidget.earnedRewards || 0,
            cashbackPerc,
            isRestaurantHaving2xDineCash: isBoosterActive,
        });
    }, [
        onImpressionCashBackEarned,
        eventId,
        dealCategory,
        restaurantId,
        billAmount,
        rewardsWidget.earnedRewards,
        cashbackPerc,
        isBoosterActive,
    ]);

    const handleCashBackToggleImpression = useCallback(() => {
        onImpressionCashBackToggle({
            eventId,
            dealCategory,
            restaurantId,
            billAmount,
            cashbackToSpend: rewardsWidget.redeemableRewards || 0,
            cashbackToEarn: rewardsWidget.earnedRewards || 0,
            toggleState: isSelected ? "on" : "off",
            remainingBalance: rewardsWidget.redeemableRewards || 0,
            cashbackPerc,
            couponApplied,
            couponDiscount,
            isRestaurantHaving2xDineCash: isBoosterActive,
        });
    }, [
        eventId,
        dealCategory,
        restaurantId,
        billAmount,
        isSelected,
        cashbackPerc,
        couponApplied,
        couponDiscount,
        onImpressionCashBackToggle,
        isBoosterActive,
        rewardsWidget.redeemableRewards,
        rewardsWidget.earnedRewards,
    ]);

    let toggleThumbColor: keyof IColors | undefined;
    if (isScenesFlow) {
        toggleThumbColor = isSelected ? "text_Button_Primary" : "text_disabled";
    }

    const rewardsContent = (
        <RewardsWidgetContent
            backgroundImage={backgroundImage}
            earnedRewards={earnedRewards}
            earnRewardsTitle={earnRewardsTitle}
            disableEarnRewardsTitle={disableEarnRewardsTitle}
            logo={logo}
            redeemableRewards={redeemableRewards}
            subtitle={subtitle}
            title={title}
            extraInfoText={extraInfoText}
            isBoosterActive={isBoosterActive}
            isToggleDisabled={isToggleDisabled}
            isSelected={isSelected}
            isScenesFlow={isScenesFlow}
            bottomNudgeTextCallout={bottomNudgeTextCallout}
            toggleThumbColor={toggleThumbColor}
            onToggleSwitch={onToggleSwitch}
            onCashBackEarnedImpression={handleCashBackEarnedImpression}
            onCashBackToggleImpression={handleCashBackToggleImpression}
        />
    );

    return (
        <>
            {isBoosterActive ? (
                <BoosterWidget
                    backgroundImage={boosterBackgroundImage}
                    dinecash2xLogoUrl={header?.logo}
                    calloutIconUrl={header?.callout}
                    boosterAnimation={boosterAnimation}
                    useRewardTitle={title}
                    headerTitle={header?.title}
                    headerSubtitle={header?.subtitle}
                >
                    {rewardsContent}
                </BoosterWidget>
            ) : (
                rewardsContent
            )}
        </>
    );
};

export default memo(RewardsWidget);
